/**
 * DeliverablesPanel — fichiers livrables digitaux d'une variante (S2.5d).
 *
 * Affiché sur `WorkDetailPage` pour chaque variante dont au moins un
 * format est `kind='digital'`. Liste les rows `work_media kind='deliverable'`
 * avec presigned URL TTL=300s et bouton "Régénérer" (utile post master
 * replace ou changement de format dans la variante).
 *
 * Pour chaque paire (variante, format), le BE génère 2 livrables :
 * 1 JPG q100 + 1 PDF. On groupe par format dans l'UI.
 */

import { RefreshCw, Download, FileImage, FileText } from 'lucide-react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useRegenerateDeliverables, useVariantDeliverables } from '@/hooks/useWorks'
import type { WorkDeliverableResponse, WorkVariantWithCatalogResponse } from '@/types/works'

import './DeliverablesPanel.css'

interface DeliverablesPanelProps {
  workId: number
  variant: WorkVariantWithCatalogResponse
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} Mo`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} Go`
}

function MimeIcon({ mime }: { mime: string }) {
  if (mime === 'application/pdf') {
    return <FileText aria-hidden="true" size={16} />
  }
  return <FileImage aria-hidden="true" size={16} />
}

function deliverableLabel(d: WorkDeliverableResponse): string {
  // ex. "JPG q100 · 4960×7016 px · 12.4 Mo"
  const ext = d.mime_type === 'application/pdf' ? 'PDF' : 'JPG q100'
  const dims = d.width && d.height ? ` · ${d.width}×${d.height} px` : ''
  return `${ext}${dims} · ${formatBytes(d.bytes)}`
}

export default function DeliverablesPanel({ workId, variant }: DeliverablesPanelProps) {
  const { confirm } = useConfirm()
  const regenerate = useRegenerateDeliverables()

  // Refonte POD v6.1 : canal de vente porté par `medium_kind`.
  const isDigital = variant.medium_kind === 'digital'
  const enabled = isDigital
  const { data: deliverables = [], isLoading } = useVariantDeliverables(workId, variant.id, enabled)

  if (!isDigital) return null

  const handleRegenerate = async () => {
    const ok = await confirm({
      title: 'Régénérer les livrables ?',
      message:
        'Tous les fichiers existants seront supprimés du stockage et regénérés depuis le master actuel. Utile après ré-upload du master ou changement des formats inclus.',
      variant: 'danger',
      confirmText: 'Régénérer',
    })
    if (!ok) return
    regenerate.mutate(
      { workId, variantId: variant.id },
      {
        onSuccess: (rows) =>
          toast.success(
            `${rows.length} fichier${rows.length > 1 ? 's' : ''} régénéré${rows.length > 1 ? 's' : ''}`,
          ),
        onError: (err) => toast.error(err.detail ?? 'Régénération impossible'),
      },
    )
  }

  // Groupe par format pour grouper visuellement les pairs JPG + PDF
  const byFormat = new Map<number, WorkDeliverableResponse[]>()
  for (const d of deliverables) {
    const fmtId = d.format_id
    const list = byFormat.get(fmtId) ?? []
    list.push(d)
    byFormat.set(fmtId, list)
  }

  // Index format_id → label depuis le format singulier de la variante.
  // Refonte POD v6.1 : 1 variante = 1 format publiable (la junction
  // multi-formats `work_variant_formats` a disparu).
  const formatLabelMap = new Map<number, string>()
  const f = variant.format
  if (f) {
    const dims = `${f.width_cm}×${f.height_cm} cm`
    formatLabelMap.set(f.format_id, f.label_fr ? `${f.label_fr} (${dims})` : dims)
  }

  return (
    <section className="deliverables-panel" aria-label="Fichiers livrables digitaux">
      <header className="deliverables-panel-header">
        <div>
          <h4 className="ui-h5 deliverables-panel-title">Livrables digitaux</h4>
          <p className="ui-body ui-text-muted deliverables-panel-subtitle">
            Fichiers générés à 300 DPI depuis le master · {deliverables.length} fichier
            {deliverables.length > 1 ? 's' : ''} prêt
            {deliverables.length > 1 ? 's' : ''} à livrer côté client.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRegenerate}
          loading={regenerate.isPending}
          aria-label="Régénérer les livrables"
        >
          <RefreshCw aria-hidden="true" size={14} />
          Régénérer
        </Button>
      </header>

      {isLoading ? (
        <p className="deliverables-panel-loading">Chargement…</p>
      ) : deliverables.length === 0 ? (
        <p className="deliverables-panel-empty">
          Aucun fichier généré pour le moment. Si la variante est active et le master ≥ 3000 px côté
          court, ils devraient apparaître après quelques secondes — sinon clique « Régénérer ».
        </p>
      ) : (
        <ul className="deliverables-panel-list">
          {Array.from(byFormat.entries()).map(([formatId, files]) => (
            <li key={formatId} className="deliverables-panel-format">
              <div className="deliverables-panel-format-label">
                {formatLabelMap.get(formatId) ?? `Format #${formatId}`}
              </div>
              <ul className="deliverables-panel-files">
                {files.map((d) => (
                  <li key={d.id} className="deliverables-panel-file">
                    <MimeIcon mime={d.mime_type} />
                    <span className="deliverables-panel-file-meta">{deliverableLabel(d)}</span>
                    {d.download_url ? (
                      <a
                        href={d.download_url}
                        download={d.original_filename}
                        className="deliverables-panel-download"
                        aria-label={`Télécharger ${d.original_filename}`}
                      >
                        <Download aria-hidden="true" size={14} />
                        {d.original_filename}
                      </a>
                    ) : (
                      <span className="deliverables-panel-file-name">{d.original_filename}</span>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
